import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Search } from "lucide-react";
import LibraryFeed from "./LibraryFeed";

const SORT_CHIPS = [
  { id: "newest", label: "Newest" },
  { id: "popular", label: "Most Popular" },
  { id: "highest-rated", label: "Top Rated" },
];

// The full-screen "browse everything" mode opened from the "All Resources"
// section's "View All" link — just a search/quick-filter bar up top and the
// full result grid below, instead of squeezing the whole catalog into the
// small preview strip on the normal Library page.
const BrowseAllView = ({
  query, onQueryChange, onBack, sortBy, onSortChange,
  university, onUniversityChange, universityOptions,
  resources, saved, onSave, onOpen, onCreateQuiz,
  viewMode, onViewModeChange, loading, totalResults,
  currentUserId, onEdit,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className="min-h-screen"
    >
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-xl border-b border-border">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-4 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="w-9 h-9 rounded-xl bg-muted hover:bg-muted/70 flex items-center justify-center transition-colors flex-shrink-0"
              title="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="relative flex-1 max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder="Search all resources..."
                className="w-full h-11 pl-11 pr-4 rounded-xl bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <span className="text-xs text-muted-foreground hidden sm:block flex-shrink-0">{totalResults} results</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {SORT_CHIPS.map((chip) => (
              <button
                key={chip.id}
                onClick={() => onSortChange(chip.id)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-colors ${
                  sortBy === chip.id
                    ? "bg-primary/10 border-primary/40 text-primary"
                    : "bg-transparent border-border text-muted-foreground hover:border-border/80 hover:text-foreground"
                }`}
              >
                {chip.label}
              </button>
            ))}

            {universityOptions.length > 0 && (
              <select
                value={university}
                onChange={(e) => onUniversityChange(e.target.value)}
                className="ml-auto text-[11px] font-bold px-3 py-1.5 rounded-lg bg-transparent border border-border text-muted-foreground focus:outline-none focus:border-primary/40"
              >
                <option value="All">All Universities</option>
                {universityOptions.map((u) => (
                  <option key={u.university} value={u.university}>{u.university} ({u.count})</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <LibraryFeed
            resources={resources}
            saved={saved}
            onSave={onSave}
            onOpen={onOpen}
            onCreateQuiz={onCreateQuiz}
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            title="All Resources"
            currentUserId={currentUserId}
            onEdit={onEdit}
          />
        )}
      </div>
    </motion.div>
  );
};

export default BrowseAllView;
